import React from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';

import { GlobalContextProvider } from './components/GlobalContextProvider';
import { ResizableLayout } from './components/layout';
import { ProjectContextRequirementGuard } from './components/ProjectContextRequirementGuard';
import ProtectedRoute from './components/ProtectedRoute';
import { TopBar } from './components/TopBar';
import { UserProfileOnboardingGate } from './components/UserProfileOnboardingGate';
import { AgentJobsProvider } from './contexts/AgentJobsContext';
import { AuthProvider } from './contexts/AuthContext';
import { UserProfileProvider } from './contexts/UserProfileContext';
import { ProjectList, Generation, ProjectSetupPage, ContextManagementPage, CRAPage, C4ModellingPage, ReviewPage } from './pages';
import { AdminUsagePage } from './pages/AdminUsage/AdminUsagePage';
import { ADRContentPage } from './pages/ADRManagement';
import ADRDefinitionPage from './pages/ADRManagement/ADRDefinitionPage';
import { ReviewDetailPage } from './pages/ArchitectureReview';
import {
  AdoOAuthCallbackPage,
  AzureDevOpsHubPage,
  SendToAdoPage,
} from './pages/AzureDevOps';
import { BrownfieldConversionPage } from './pages/BrownfieldConversion';
import EntraIDCallbackPage from './pages/EntraIDCallbackPage';
import { KnowledgeBasePage, KnowledgeBaseWelcomeStep } from './pages/KnowledgeBase';
import { LineOfBusinessWelcomePage } from './pages/LineOfBusinessWelcome/LineOfBusinessWelcomePage';
import LoginPage from './pages/LoginPage';
import { UserProfilePage } from './pages/UserProfile/UserProfilePage';
import './App.css';

// Wrapper component to extract URL params for ADR page
const ADRManagementRoute: React.FC = () => {
  return <ADRDefinitionPage />;
};

const requireBusinessRequirementsDocument = (
  element: React.ReactElement,
): JSX.Element => (
  <ProjectContextRequirementGuard>{element}</ProjectContextRequirementGuard>
);

const MainLayout: React.FC = () => {
  return (
    <ProtectedRoute>
      <UserProfileProvider>
        <GlobalContextProvider>
          <AgentJobsProvider>
            <div className="App flex flex-col h-screen">
              <TopBar />
            
              {/* Main Layout Container - Resizable layout with chat */}
              <div className="flex-1 min-h-0">
                <ResizableLayout 
                  showChat={false}
                  defaultChatSize={30}
                  minChatSize={20}
                  maxChatSize={50}
                  className="h-full"
                >
                  {/* Main Content Area */}
                  <main className="h-full">
                    <UserProfileOnboardingGate>
                      <div className="App-main h-full overflow-y-auto">
                        <Outlet />
                      </div>
                    </UserProfileOnboardingGate>
                  </main>
                </ResizableLayout>
              </div>
            </div>
          </AgentJobsProvider>
        </GlobalContextProvider>
      </UserProfileProvider>
    </ProtectedRoute>
  );
};

const App: React.FC = () => {
  return (
    <div data-theme="shell" className="min-h-screen bg-base-100 flex flex-col">
      <Router>
        <AuthProvider>
          <Routes>
            {/* Public routes */}
            {/* Login page is now a fallback - automatic SSO is handled by ProtectedRoute */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<EntraIDCallbackPage />} />
            <Route path="/auth/ado-callback" element={<AdoOAuthCallbackPage />} />
            
            {/* Protected routes - automatically redirect to SSO if not authenticated */}
            <Route element={<MainLayout />}>
              <Route index element={<ProjectList />} />
              <Route path="/create" element={<ProjectSetupPage />} />
              <Route path="/project/:projectId/setup" element={<ProjectSetupPage />} />
              <Route path="/project/:projectId/context" element={<ContextManagementPage />} />
              <Route path="/project/:projectId/brownfield-conversion" element={requireBusinessRequirementsDocument(<BrownfieldConversionPage />)} />
              <Route path="/project/:projectId/cra" element={requireBusinessRequirementsDocument(<CRAPage />)} />
              <Route path="/project/:projectId/adrs" element={requireBusinessRequirementsDocument(<ADRManagementRoute />)} />
              <Route path="/project/:projectId/adr/:adrId/content" element={requireBusinessRequirementsDocument(<ADRContentPage />)} />
              <Route path="/project/:projectId/c4" element={requireBusinessRequirementsDocument(<C4ModellingPage />)} />
              <Route path="/project/:projectId/generation" element={requireBusinessRequirementsDocument(<Generation />)} />
              <Route path="/project/:projectId/review" element={requireBusinessRequirementsDocument(<ReviewPage />)} />
              <Route path="/reviews/:reviewId" element={<ReviewDetailPage />} />
              <Route path="/profile" element={<UserProfilePage />} />
              <Route path="/welcome" element={<LineOfBusinessWelcomePage />} />
              <Route path="/welcome/knowledge-base" element={<KnowledgeBaseWelcomeStep />} />
              <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
              <Route path="/admin/usage-statistics" element={<AdminUsagePage />} />
              <Route path="/azure-devops" element={<AzureDevOpsHubPage />} />
              <Route
                path="/project/:projectId/azure-devops"
                element={<SendToAdoPage />}
              />
              {/* Legacy route for backward compatibility */}
              <Route path="/generation/:projectId" element={requireBusinessRequirementsDocument(<Generation />)} />
            </Route>
          </Routes>
        </AuthProvider>
      </Router>
    </div>
  );
};

export default App;
